import { Cookie as CookieNames } from '@ps/web-core-enums';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import Label from '@swf/components/Inputs/components/Label/Label';
import Link from '@swf/components/Link/Link';
import RegulatoryCheckboxes from '@swf/components/RegulatoryCheckboxes/RegulatoryCheckboxes';
import Translation from '@swf/components/Translation/Translation';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import { checkUserEmail } from '@swf/core/utils/validators';

import { PageType } from '../../../../types/ContentOverlay';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import ContentOverlay from '../../../ContentOverlay/ContentOverlay';
import Email from '../../../Email/Email';
import Password from '../../../Password/Password';
import Username from '../../../Username/Username';
import SignupBlockedCountry from '../SignupBlockedCountry/SignupBlockedCountry';
import SignupCountryField from '../SignupCountry/components/SignupCountrySelect/components/SignupCountryField';
import MarketingMailList from '../SignupEmail/components/MarketingMailList/MarketingMailList';

import {
  TXTCLI_Country_of_Residence,
  TXTCLI_Create_Account,
  TXTWCL_Contact_Details,
  TXTWCL_Create_your_Account_todayX,
  TXTWCL_ThatXs_already_takenX_Try_Xcta_ELL
} from './SingleScreenSignUp.messages.json';
import { signupCountryField } from './SingleScreenSignUp.scss';

const cookies = new Cookies();

const SingleScreenSignUp: React.FC = () => {
  const request = useRequest();
  const { installId } = useSelector(select.session.get);
  const options = useSelector(select.cms.getRequestOptions);
  const [pageType, setPageType] = useState<PageType>();
  const [isChosenCountryValid, setIsChosenCountryValid] = useState(true);
  const onEulaLinkClick = () => setPageType(PageType.Eula);

  const signupCountryCheck = useCallback(
    async (newCountry: { value: string; text: string }) => {
      if (!newCountry?.value) return;
      const installId = cookies.get(CookieNames.INSTALL_ID);
      request({
        service: '/api/v1-preview/registration/country/validate',
        method: 'POST',
        http: true,
        body: { installId, country: newCountry.value }
      }).subscribe(({ body: isCountryAvailable }) => setIsChosenCountryValid(isCountryAvailable));
    },
    [request]
  );

  const onResetCountryValidation = () => setIsChosenCountryValid(true);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isChosenCountryValid ? (
        <Container
          page={SingleScreenSignUp}
          headerProps={{
            title: <Translation {...TXTWCL_Contact_Details} />
          }}
          pageTopTitle={<Translation {...TXTWCL_Create_your_Account_todayX} />}
        >
          <div className={signupCountryField}>
            <Label id="residence-country">
              <Translation {...TXTCLI_Country_of_Residence} />
            </Label>
            <SignupCountryField
              submitOnSelectionChange={false}
              onCountryChange={signupCountryCheck}
              showBorders
            />
          </div>
          <Username />
          <Password />
          <Email
            id="email"
            validators={[
              checkUserEmail(options, installId, {
                ...TXTWCL_ThatXs_already_takenX_Try_Xcta_ELL,
                values: {
                  cta: (text: string) => <Link href="/login">{text}</Link>
                }
              })
            ]}
          />
          <MarketingMailList />
          <RegulatoryCheckboxes
            variant="first"
            onEulaLinkClick={onEulaLinkClick}
            onPrivacyLinkClick={() => setPageType(PageType.Privacy)}
          />
          <CTAButton translation={TXTCLI_Create_Account} />
          {pageType && (
            <ContentOverlay
              {...(pageType === PageType.Eula && { sectionId: 'part-B-section6' })}
              pageType={pageType}
              onClose={() => setPageType(undefined)}
            />
          )}
        </Container>
      ) : (
        <SignupBlockedCountry onBackClick={onResetCountryValidation} />
      )}
    </>
  );
};

SingleScreenSignUp.displayName = 'SingleScreenSignUp';

export default SingleScreenSignUp;
